import { useCallback, useMemo, useRef } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { IconInfoCircle } from "@tabler/icons-react";
import { Typography, Stack, Card, IconButton } from "@altimateai/lego";

dayjs.extend(utc);
import { DateRange } from "@/components/dateRange";
import { FlexListSummary } from "@/components/listSummary";
import { useAppState } from "@/modules/app/useAppContext";
import { formatBytes, formatCost } from "@/helpers/utils";
import { formatNumber } from "@/uiCore";
import { getDatabricksQueryStatementIds } from "@/helpers/apis";
import type { APIFilterParams } from "@/components/tables/types";
import {
  useDatabricksQuerySummary,
  useDatabricksUnifiedQueries,
  useDatabricksQueryFilters,
} from "../hooks";
import { toOptions } from "../helpers";
import { buildComputeFilterOptions } from "./utils";
import { QueryTable } from "./QueryTable";
import { useDatabricksQueriesFilters } from "./useDatabricksQueriesFilters";
import { useRegisterDatabricksQueryFilters } from "./useRegisterDatabricksQueryFilters";
import { useBreadcrumb } from "@/components/breadcrumbs";

const DatabricksQueriesContent = () => {
  useBreadcrumb("Queries");
  const { currency } = useAppState();
  // Capture page-load time once so it doesn't re-generate on re-renders.
  const pageLoadTimestamp = useRef(dayjs.utc().format("HH:mm:ss [UTC on] DD/MM/YYYY"));
  const {
    page,
    pageSize,
    startDate,
    endDate,
    onDateUpdate,
    dateRange,
    workspaceId,
    statementTypeFilter,
    userNameFilter,
    statementIdFilter,
    warehouseIdFilter,
    computeFilter,
    workspaceIdFilter,
    sortAttr,
    sortOrder,
    setSort,
  } = useDatabricksQueriesFilters();

  // Shared filter params applied to both the summary hero cards and the table.
  // This keeps the summary in sync with whatever the user has filtered on.
  const queryParams = {
    start_date: dateRange.start_date,
    end_date: dateRange.end_date,
    workspace_id: workspaceId,
    workspace_ids: workspaceIdFilter.length > 0 ? workspaceIdFilter : undefined,
    compute_id: computeFilter.length > 0 ? computeFilter : undefined,
    statement_type: statementTypeFilter || undefined,
    user_name: userNameFilter.length > 0 ? userNameFilter : undefined,
    statement_ids: statementIdFilter.length > 0 ? statementIdFilter : undefined,
  };

  // Fetch filter options
  const { data: filterOptionsData } = useDatabricksQueryFilters(
    { workspace_id: workspaceId, ...dateRange },
    true
  );

  // Register the column-less More-Filters entries (User, Workspace).
  // Compute and Query Type remain column-header filters; statement IDs
  // is server-search on the Query ID column.
  useRegisterDatabricksQueryFilters(filterOptionsData);

  const { data: summaryData, isLoading: summaryLoading } = useDatabricksQuerySummary(queryParams);

  // ``sortAttr`` / ``sortOrder`` always carry the page's canonical
  // defaults (set in ``useDatabricksQueriesFilters``), so we don't
  // need a falsy fallback before passing them through.
  const { data: unifiedData, isFetching: unifiedFetching } = useDatabricksUnifiedQueries({
    ...queryParams,
    // ``warehouse_id`` stays for back-compat with bookmarks that used the
    // old warehouse-only column filter. The unified ``compute_id`` values
    // (prefixed ``{kind}:{id}``) are already in ``queryParams``. BE ORs
    // both, so sending both is safe — a selection in either path matches.
    warehouse_id: warehouseIdFilter.length > 0 ? warehouseIdFilter : undefined,
    page: page + 1,
    page_size: pageSize,
    sort_by: sortAttr,
    sort_order: sortOrder,
  });

  // Annualized Total Cost is computed per-query-identity on the backend,
  // so the FE doesn't multiply ``estimated_cost_usd`` by a global
  // ``365 / range_days`` scale factor any more.

  // Server-side search for the Query ID dropdown — the typed text is
  // sent as ``prefix`` (scoped to the date range), so the full
  // statement-ID list is never loaded at once. The raw fetcher is
  // passed through without local debouncing or stale-response
  // bookkeeping, mirroring the other API-dropdown consumers in this
  // app.
  const queryIdSearchFn = useCallback(
    ({ prefix, page: searchPage, size }: APIFilterParams) =>
      getDatabricksQueryStatementIds({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        workspace_id: workspaceId,
        prefix: prefix || undefined,
        page: searchPage,
        page_size: size,
      }),
    [dateRange, workspaceId]
  );

  const statementTypeOptions = useMemo(
    () => toOptions(filterOptionsData?.statement_types),
    [filterOptionsData]
  );

  // The Compute column filter binds to the unified ``computes`` list
  // (warehouse + cluster + pipeline). ``buildComputeFilterOptions``
  // produces ``{label, value}`` entries — ``value`` is the prefixed
  // ``{kind}:{id}`` string the BE's ``compute_id`` param expects;
  // ``label`` is ``display`` verbatim, with a ``(<raw id>)`` suffix
  // appended on same-name collisions so the dropdown stays
  // distinguishable. See the helper's docstring for the rationale.
  const computeOptions = useMemo(
    () => buildComputeFilterOptions(filterOptionsData?.computes ?? []),
    [filterOptionsData]
  );

  const summary = summaryData?.summary;
  // Loading has finished but no summary came back — either an HTTP error or
  // a null payload. Surface it rather than letting FlexListSummary render a
  // misleading $0 / 0 (FlexListSummary has no error state of its own).
  const summaryUnavailable = !summaryLoading && !summary;
  const totalQueries = summary?.total_queries ?? 0;
  const summaryItems = useMemo(
    () => [
      { label: "Total Costs", value: formatCost(summary?.total_cost ?? 0, currency) },
      {
        // Compact-formatted to match the other tiles (``Total Costs`` /
        // ``Total Data Read`` are bytes/currency-compact); the exact count
        // stays available on hover for the curious. ``toLocaleString("en")``
        // pins the thousands separator so CI snapshots aren't locale-sensitive.
        // ``tooltip`` is wired through the shared ``FlexListSummary`` config
        // so the page doesn't need to know about the Tooltip component.
        label: "Total Queries",
        value: formatNumber(totalQueries),
        tooltip: totalQueries.toLocaleString("en"),
      },
      { label: "Total Data Read", value: formatBytes(summary?.total_read_bytes ?? 0) },
      { label: "Total Data Spilled", value: formatBytes(summary?.total_spilled_bytes ?? 0) },
    ],
    [summary, currency, totalQueries]
  );

  return (
    <div className="al-bg-[var(--vz-body-bg)] al--mt-8">
      {/* Page header row */}
      <div className="al-py-4 al--ml-px al-w-full">
        <Stack direction="row" align="center" justify="between" className="al-w-full al-px-4">
          <Stack align="center">
            <Typography variant="page-heading">Queries</Typography>
            {/* Last-updated tooltip — matches the pattern used by
                ``AllQueriesHeader`` / ``GroupsHeader`` so the heading
                stays a single line and the timestamp is reachable on
                hover instead of taking a dedicated row.
                Uses the lego ``IconButton`` (Button with icon size /
                ghost variant baked in) so the trigger stays an
                accessible focusable ``<button>`` while still wrapping
                the icon in a tooltip via the ``title`` prop. */}
            <IconButton
              title={`The data was last updated at ${pageLoadTimestamp.current}`}
              aria-label={`Last updated at ${pageLoadTimestamp.current}`}
            >
              <IconInfoCircle size={18} className="al-text-gray-500" />
            </IconButton>
          </Stack>
          <Stack direction="row" align="center" gap="sm">
            <DateRange
              startDate={startDate}
              endDate={endDate}
              onDateRangeSelect={onDateUpdate}
              id="queries-date-range"
              maxDate={new Date()}
            />
            <div id="databricks-queries-filter-container" />
          </Stack>
        </Stack>
      </div>

      <Stack direction="column" gap="md" className="al-px-4 al-pb-6">
        {/* Hero summary */}
        {summaryUnavailable ? (
          <Card className="al-border-[#d5dbe4] al-rounded-lg al-shadow-xs al-p-4">
            <Typography className="al-text-red-500">Failed to load query summary</Typography>
          </Card>
        ) : (
          <FlexListSummary isLoading={summaryLoading} summary={summaryItems} />
        )}

        {/* Queries table */}
        <Card className="al-border-[#d5dbe4] al-rounded-lg al-shadow-xs al-overflow-hidden">
          <div className="al-p-6">
            <QueryTable
              data={unifiedData}
              isFetching={unifiedFetching}
              page={page}
              pageSize={pageSize}
              sortAttr={sortAttr}
              sortOrder={sortOrder}
              statementTypeFilter={statementTypeFilter}
              statementIdFilter={statementIdFilter}
              computeFilter={computeFilter}
              statementTypeOptions={statementTypeOptions}
              computeOptions={computeOptions}
              queryIdSearchFn={queryIdSearchFn}
              onSortChange={setSort}
            />
          </div>
        </Card>
      </Stack>
    </div>
  );
};

export default DatabricksQueriesContent;
